import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GOOGLE_PLACES_SCRIPT_ID = "fleetx-google-places-script";
const googlePlacesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let googlePlacesLoader = null;

function loadGooglePlaces() {
  if (typeof window === "undefined" || !googlePlacesApiKey) {
    return Promise.resolve(null);
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps.places);
  }

  if (googlePlacesLoader) return googlePlacesLoader;

  googlePlacesLoader = new Promise((resolve) => {
    const existingScript = document.getElementById(GOOGLE_PLACES_SCRIPT_ID);
    const finish = () => resolve(window.google?.maps?.places || null);

    if (existingScript) {
      existingScript.addEventListener("load", finish, { once: true });
      existingScript.addEventListener("error", () => resolve(null), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_PLACES_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      googlePlacesApiKey
    )}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = finish;
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

  return googlePlacesLoader;
}

function getLatLng(place) {
  const location = place?.geometry?.location;
  if (!location) return null;

  const lat = typeof location.lat === "function" ? location.lat() : location.lat;
  const lng = typeof location.lng === "function" ? location.lng() : location.lng;
  return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
    ? { lat: Number(lat), lng: Number(lng) }
    : null;
}

export default function GooglePlacesAutocomplete({
  id,
  name,
  value,
  placeholder,
  className = "",
  inputClassName = "",
  onChange,
  onPlaceSelect,
  apiMode = "legacy",
}) {
  const [placesReady, setPlacesReady] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const skipBlur = useRef(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const dismissedValue = useRef("");
  const requestId = useRef(0);
  const sessionToken = useRef(null);

  useEffect(() => {
    let mounted = true;
    loadGooglePlaces().then((places) => {
      if (!mounted || !places) return;
      autocompleteService.current = new places.AutocompleteService();
      placesService.current = new places.PlacesService(document.createElement("div"));
      if (apiMode === "new" && places.AutocompleteSessionToken) {
        sessionToken.current = new places.AutocompleteSessionToken();
      }
      setPlacesReady(true);
    });
    return () => {
      mounted = false;
    };
  }, [apiMode]);

  const closeSuggestions = useCallback(() => {
    requestId.current += 1;
    setIsOpen(false);
    setActiveIndex(-1);
    setPredictions([]);
  }, []);

  const dismissCurrentValue = useCallback(() => {
    dismissedValue.current = String(value || "").trim();
    closeSuggestions();
  }, [closeSuggestions, value]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (wrapperRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      dismissCurrentValue();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [dismissCurrentValue]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const positionMenu = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuStyle({
        position: "fixed",
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
      });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const nextValue = String(value || "").trim();

    if (
      !placesReady ||
      !nextValue ||
      nextValue.length < 2 ||
      nextValue === dismissedValue.current
    ) {
      setPredictions([]);
      return undefined;
    }

    const activeRequestId = requestId.current + 1;
    requestId.current = activeRequestId;

    const timer = window.setTimeout(() => {
      if (apiMode === "new" && window.google?.maps?.places?.AutocompleteSuggestion) {
        window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
          includedRegionCodes: ["ph"],
          sessionToken: sessionToken.current,
        }).then(({ suggestions = [] }) => {
          if (activeRequestId !== requestId.current) return;
          setPredictions(suggestions.map(({ placePrediction }) => ({
            description: placePrediction?.text?.toString() || String(),
            place_id: placePrediction?.placeId || String(),
            placePrediction,
          })).filter((prediction) => prediction.place_id).slice(0, 5));
          setActiveIndex(-1);
          setIsOpen(suggestions.length > 0);
        }).catch(() => setPredictions([]));
        return;
      }

      autocompleteService.current?.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: "ph" },
        },
        (results, status) => {
          if (activeRequestId !== requestId.current) return;
          if (status !== window.google?.maps?.places?.PlacesServiceStatus?.OK || !results) {
            setPredictions([]);
            return;
          }
          setPredictions(results.slice(0, 5));
          setIsOpen(true);
        }
      );
    }, 250);

    return () => window.clearTimeout(timer);
  }, [apiMode, placesReady, value]);

  const handleInputChange = (event) => {
    dismissedValue.current = "";
    onChange(event.target.value);
    if (!event.target.value.trim()) {
      closeSuggestions();
    } else {
      setIsOpen(true);
    }
  };

  const completeSelection = ({ address, coords, placeId }) => {
    dismissedValue.current = String(address || "").trim();
    closeSuggestions();
    onPlaceSelect({ address, coords, placeId });
    inputRef.current?.blur();
  };

  const selectPrediction = (prediction) => {
    closeSuggestions();

    if (apiMode === "new" && prediction.placePrediction?.toPlace) {
      const place = prediction.placePrediction.toPlace();
      place.fetchFields({
        fields: ["formattedAddress", "location", "id", "displayName"],
      }).then(() => {
        completeSelection({
          address: place.formattedAddress || prediction.description || place.displayName || String(),
          coords: place.location
            ? { lat: Number(place.location.lat()), lng: Number(place.location.lng()) }
            : null,
          placeId: place.id || prediction.place_id,
        });
        const Token = window.google?.maps?.places?.AutocompleteSessionToken;
        sessionToken.current = Token ? new Token() : null;
      }).catch(() => completeSelection({
        address: prediction.description,
        coords: null,
        placeId: prediction.place_id,
      }));
      return;
    }

    if (!placesService.current) {
      completeSelection({
        address: prediction.description,
        coords: null,
        placeId: prediction.place_id,
      });
      return;
    }

    placesService.current?.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["formatted_address", "geometry", "place_id", "name"],
      },
      (place, status) => {
        if (status !== window.google?.maps?.places?.PlacesServiceStatus?.OK || !place) {
          completeSelection({
            address: prediction.description,
            coords: null,
            placeId: prediction.place_id,
          });
          return;
        }

        completeSelection({
          address: place.formatted_address || prediction.description || place.name || "",
          coords: getLatLng(place),
          placeId: place.place_id || prediction.place_id,
        });
      }
    );
  };

  const handleKeyDown = (event) => {
    if (!isOpen || predictions.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        if (current < 0) return direction > 0 ? 0 : predictions.length - 1;
        return (current + direction + predictions.length) % predictions.length;
      });
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      selectPrediction(predictions[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      dismissCurrentValue();
    }
  };

  return (
    <div ref={wrapperRef} className={`places-autocomplete ${apiMode === 'new' ? '' : className}`}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        className={inputClassName}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (String(value || "").trim() !== dismissedValue.current) {
            setIsOpen(predictions.length > 0);
          }
        }}
        onBlur={() => {
          window.setTimeout(() => {
            if (!skipBlur.current) dismissCurrentValue();
            skipBlur.current = false;
          }, 120);
        }}
      />
      {placesReady && isOpen && predictions.length > 0 && menuStyle ? createPortal(
        <div ref={menuRef} className="places-autocomplete__menu" style={menuStyle}>
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              className={`places-autocomplete__option ${index === activeIndex ? 'is-active' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={() => {
                skipBlur.current = true;
              }}
              onClick={() => selectPrediction(prediction)}
            >
              {prediction.description}
            </button>
          ))}
          <div className="places-autocomplete__powered">Suggestions powered by Google</div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}
